/**
 * Sysadmin Tenant Detail — Audit tab (R5 §4.9).
 *
 * Spec §4.9: pre-filtered audit stream + same chain-integrity widget
 * as the global /admin/audit page, scoped to this tenant.
 *
 * R5 fix (2026-05-13): adds the chain integrity widget at the top of
 * the tab. The audit list itself still reads off the aggregated
 * detail payload (no extra round-trip) but pagination + full audit
 * stream is tracked as a follow-up (P2-6 from the audit report).
 */

import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ShieldCheck, ShieldAlert, Hash, Clock, Copy, KeyRound, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTenantDetail, useTenantAuditIntegrity } from '@/lib/use-tenants';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/audit')({
  component: TenantAuditTab,
});

function TenantAuditTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/audit' });
  const { data } = useTenantDetail(tenantId);
  const integrity = useTenantAuditIntegrity(tenantId);

  if (!data) return null;
  const { recentAudit } = data;

  function copy(value: string, label: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    }
  }

  return (
    <div className="space-y-5">
      {/* R5 §4.9 — chain integrity widget */}
      <TenantAuditIntegrityWidget
        loading={integrity.isLoading}
        error={integrity.error}
        status={integrity.data}
        onRefetch={() => integrity.refetch()}
        onCopy={copy}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent audit (last 25)</CardTitle>
          <CardDescription>
            Tenant-scoped <code>audit_logs</code> entries. Full
            pagination + filter UI lands with the audit-module
            integration follow-up.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {recentAudit.length === 0 ? (
            <div className="px-6 py-6 text-sm text-muted-foreground">
              No audit entries yet for this tenant.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Outcome</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentAudit.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(r.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.action}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline">{r.category}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.userEmail ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant={r.outcome === 'success' ? 'default' : 'destructive'}>
                        {r.outcome}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Tenant-scoped chain integrity widget — R5 §4.9.
 *
 * Mirrors the global /admin/audit/integrity status panel but scoped to
 * the tenant's audit_logs hash chain. Surfaces:
 *   • chain head sequence + hash (with copy-to-clipboard)
 *   • witness record sync timestamp (platform key, shared across all
 *     tenants per audit-integrity.service.ts)
 *   • health badge — green if last-100-row quick walk verifies; red
 *     "Chain BROKEN at seq X" banner otherwise
 *   • refresh button — re-runs the read (no full verify-now; that
 *     remains on the global page)
 */
function TenantAuditIntegrityWidget({
  loading, error, status, onRefetch, onCopy,
}: {
  loading: boolean;
  error: Error | null;
  status: import('@/lib/use-tenants').TenantAuditIntegrityStatus | undefined;
  onRefetch: () => void;
  onCopy: (value: string, label: string) => void;
}) {
  const broken = status?.brokenChainBanner.active === true;

  return (
    <Card className={broken ? 'border-red-500/40' : undefined}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {broken ? (
                <ShieldAlert className="h-4 w-4 text-red-600" />
              ) : (
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
              )}
              Audit chain integrity
            </CardTitle>
            <CardDescription>
              Tenant-scoped hash chain. Tamper-evident audit ledger;
              same algorithm + witness key as the global audit page.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefetch}
            disabled={loading}
            title="Re-check chain head"
            className="h-7"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-sm text-destructive">Failed to load: {error.message}</div>
        ) : loading || !status ? (
          <div className="text-sm text-muted-foreground animate-pulse">Loading chain head…</div>
        ) : (
          <div className="space-y-3">
            {broken && (
              <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-400">
                  <ShieldAlert className="h-4 w-4" />
                  Chain BROKEN at sequence {status.brokenChainBanner.brokenAt}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Reason: <code>{status.brokenChainBanner.reason ?? 'unknown'}</code>.
                  Re-verify from the global Audit module.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded border p-3">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
                  <Hash className="h-3 w-3" />
                  Chain head
                </div>
                <div className="text-2xl font-semibold tabular-nums mt-0.5">
                  {status.chainHeadSeq.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  events recorded
                </div>
                {status.chainHeadExists && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <code className="text-[10px] font-mono break-all flex-1 truncate">
                      {status.chainHeadHash.slice(0, 16)}…
                    </code>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5 shrink-0"
                      onClick={() => onCopy(status.chainHeadHash, 'Chain head hash')}
                      title="Copy full hash"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="rounded border p-3">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
                  <KeyRound className="h-3 w-3" />
                  Last witness
                </div>
                {status.lastWitnessAt ? (
                  <>
                    <div className="text-sm font-medium mt-1">
                      {new Date(status.lastWitnessAt).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      seq {status.lastWitnessSeq?.toLocaleString() ?? '—'}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground mt-1">
                    No attestation yet
                  </div>
                )}
                {status.witnessKeyId && (
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                    {status.witnessKeyId}
                  </div>
                )}
              </div>

              <div className="rounded border p-3">
                <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Health
                </div>
                <div className="mt-1.5">
                  {status.isHealthy ? (
                    <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                      Healthy
                    </Badge>
                  ) : (
                    <Badge variant="destructive">Broken</Badge>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
                  Last-100-row quick walk. Full verify lives on the
                  global audit page.
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
